import { Options } from '@crm/api'
import { extractFormFieldIds } from '@crm/utils'
import { TDataShape } from '@hey-api/client-axios'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Form, Modal, ModalProps } from 'antd'
import { FormInstance } from 'antd/lib'
import { AxiosError } from 'axios'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import type {
  DefaultError,
  UseMutationOptions,
  UseQueryOptions
} from '@tanstack/react-query'

type Props<
  TData extends TDataShape,
  TDataReadable,
  TDataCreate extends TDataShape,
  TDataCreateResponse,
  TDataUpdate extends TDataShape,
  TDataUpdateResponse
> = {
  id?: number
  formName: string
  getItem?: (options: Options<TData>) => UseQueryOptions<
    TDataReadable,
    Error,
    TDataReadable,
    [
      Options<TData> & {
        _id: string
        _infinite?: boolean
      }
    ]
  >
  onCreate?: (
    options?: Partial<Options<TDataCreate>>
  ) => UseMutationOptions<
    TDataCreateResponse,
    AxiosError<DefaultError>,
    Options<TDataCreate>
  >
  onUpdate: (
    options?: Partial<Options<TDataUpdate>>
  ) => UseMutationOptions<
    TDataUpdateResponse,
    AxiosError<DefaultError>,
    Options<TDataUpdate>
  >
  onCloseModal?: () => void
  form?: FormInstance
  onFinish?: () => void
} & ModalProps

export const FormModal = <
  TData extends TDataShape,
  TDataReadable,
  TDataCreate extends TDataShape,
  TDataCreateResponse,
  TDataUpdate extends TDataShape,
  TDataUpdateResponse
>({
  id,
  formName,
  children,
  getItem,
  onCreate,
  onUpdate,
  onCloseModal,
  onFinish,
  ...props
}: Props<
  TData,
  TDataReadable,
  TDataCreate,
  TDataCreateResponse,
  TDataUpdate,
  TDataUpdateResponse
>) => {
  const { t } = useTranslation()

  const queryClient = useQueryClient()
  const [antdForm] = Form.useForm()
  const { notification } = App.useApp()

  const form = props.form || antdForm

  const mutateFn = id ? onUpdate : onCreate

  const queryOptions =
    getItem && id
      ? {
          ...getItem({ path: { id } } as Options<TData>),
          enabled: !!id
        }
      : {
          queryKey: [],
          queryFn: () => Promise.resolve(null),
          enabled: false
        }

  const { data, isLoading } = useQuery(queryOptions as UseQueryOptions)

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ ...data, ...extractFormFieldIds(data) })
    }
  }, [data, form])

  const { mutate, isPending } = useMutation({
    ...(mutateFn?.() as UseMutationOptions<
      TDataCreateResponse | TDataUpdateResponse,
      AxiosError<DefaultError> & Error,
      Options<TDataCreate | TDataUpdate>
    >),
    onSuccess: () => {
      queryClient.clear()

      onCloseModal?.()
      form.resetFields()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    },
    onError: (error) => showApiErrors(error, form)
  })

  return (
    <Modal
      afterClose={form.resetFields}
      loading={isLoading}
      title={t(!id ? 'nomenclature.add_entity' : 'nomenclature.edit_entity')}
      centered
      okButtonProps={{
        htmlType: 'submit',
        form: formName,
        loading: isPending
      }}
      onCancel={onCloseModal}
      {...props}
    >
      <Form
        name={formName}
        form={form}
        onFinish={(data) => {
          if (onFinish) {
            onFinish()
          } else {
            mutate({
              path: { id: Number(id) },
              body: data
            } as Options<TDataUpdate>)
          }
        }}
        layout="vertical"
      >
        {children}
      </Form>
    </Modal>
  )
}
